import { useEffect, useState as useReactState, useRef } from 'react';
import { create } from 'zustand';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';

type AlertRequest = {
  id: number;
  kind: 'alert';
  message: string;
  title?: string;
  resolve: () => void;
};

type ConfirmRequest = {
  id: number;
  kind: 'confirm';
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  resolve: (ok: boolean) => void;
};

type PromptRequest = {
  id: number;
  kind: 'prompt';
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  defaultValue?: string;
  placeholder?: string;
  resolve: (value: string | null) => void;
};

type DialogRequest = AlertRequest | ConfirmRequest | PromptRequest;

const useDialogStore = create(() => ({
  current: null as DialogRequest | null,
}));

const queue: DialogRequest[] = [];
let nextId = 1;
/** True while a deferred present is waiting to paint (avoids double-open races). */
let opening = false;

/**
 * Delay before a confirm primary button accepts input. Opening from Enter on a
 * Delete control can otherwise land the same keyup on the new primary button.
 */
export const CONFIRM_ARM_MS = 150;

const present = (request: DialogRequest) => {
  const show = () => {
    opening = false;
    if (useDialogStore.getState().current) {
      queue.push(request);
      return;
    }
    useDialogStore.setState({ current: request });
  };

  if (useDialogStore.getState().current || opening) {
    queue.push(request);
    return;
  }

  // Defer past the opener's click/keydown so the new dialog is not in the DOM
  // for the rest of that gesture.
  opening = true;
  window.setTimeout(show, 0);
};

const advance = () => {
  useDialogStore.setState({ current: queue.shift() ?? null });
};

export type AlertDialogOptions = {
  title?: string;
};

/** Blocking message. Resolves when the reviewer dismisses it. */
export const alertDialog = (message: string, opts: AlertDialogOptions = {}): Promise<void> =>
  new Promise(resolve => {
    present({
      id: nextId++,
      kind: 'alert',
      message,
      title: opts.title,
      resolve: () => {
        advance();
        resolve();
      },
    });
  });

export type ConfirmDialogOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
};

/** Yes / no. Resolves `true` on confirm, `false` on cancel. */
export const confirmDialog = (message: string, opts: ConfirmDialogOptions = {}): Promise<boolean> =>
  new Promise(resolve => {
    present({
      id: nextId++,
      kind: 'confirm',
      message,
      title: opts.title,
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      tone: opts.tone,
      resolve: ok => {
        advance();
        resolve(ok);
      },
    });
  });

export type PromptDialogOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  defaultValue?: string;
  placeholder?: string;
};

/** Typed input. Resolves the string on confirm, or `null` on cancel. */
export const promptDialog = (message: string, opts: PromptDialogOptions = {}): Promise<string | null> =>
  new Promise(resolve => {
    present({
      id: nextId++,
      kind: 'prompt',
      message,
      title: opts.title,
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      defaultValue: opts.defaultValue,
      placeholder: opts.placeholder,
      resolve: value => {
        advance();
        resolve(value);
      },
    });
  });

const AlertHost = ({ request }: { request: AlertRequest }) => {
  const settled = useRef(false);
  const actionRef = useRef<HTMLButtonElement>(null);

  const finish = () => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    request.resolve();
  };

  return (
    <AlertDialog
      open
      onOpenChange={next => {
        if (!next) {
          finish();
        }
      }}
    >
      <AlertDialogContent
        onOpenAutoFocus={event => {
          event.preventDefault();
          actionRef.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{request.title ?? 'Notice'}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-wrap">{request.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction ref={actionRef} onClick={finish}>
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Confirm uses plain Dialog + Button (not AlertDialogAction). Radix Action
 * auto-closes and is easy to ghost-activate from the opener's Enter keyup.
 */
const ConfirmHost = ({ request }: { request: ConfirmRequest }) => {
  const settled = useRef(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [armed, setArmed] = useReactState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setArmed(true), CONFIRM_ARM_MS);
    return () => window.clearTimeout(id);
  }, []);

  const finish = (ok: boolean) => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    request.resolve(ok);
  };

  return (
    <Dialog
      open
      onOpenChange={next => {
        if (!next) {
          finish(false);
        }
      }}
    >
      <DialogContent
        hideClose
        className="max-w-md"
        onOpenAutoFocus={event => {
          event.preventDefault();
          cancelRef.current?.focus();
        }}
        onInteractOutside={event => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{request.title ?? 'Confirm'}</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">{request.message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button ref={cancelRef} type="button" variant="outline" onClick={() => finish(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            type="button"
            variant={request.tone === 'danger' ? 'danger' : 'primary'}
            disabled={!armed}
            onClick={() => finish(true)}
          >
            {request.confirmLabel ?? 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PromptHost = ({ request }: { request: PromptRequest }) => {
  const [value, setValue] = useReactState(request.defaultValue ?? '');
  const settled = useRef(false);

  const finish = (result: string | null) => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    request.resolve(result);
  };

  return (
    <Dialog
      open
      onOpenChange={next => {
        if (!next) {
          finish(null);
        }
      }}
    >
      <DialogContent hideClose className="max-w-md">
        <DialogHeader>
          <DialogTitle>{request.title ?? 'Input required'}</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">{request.message}</DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={request.placeholder}
          aria-label={request.title ?? 'Dialog input'}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              finish(value);
            }
          }}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => finish(null)}>
            {request.cancelLabel ?? 'Cancel'}
          </Button>
          <Button type="button" onClick={() => finish(value)}>
            {request.confirmLabel ?? 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Mount once next to the snack bar host. */
export const DialogGlobalComponent = () => {
  const current = useDialogStore(state => state.current);
  if (!current) {
    return null;
  }
  if (current.kind === 'prompt') {
    return <PromptHost key={current.id} request={current} />;
  }
  if (current.kind === 'confirm') {
    return <ConfirmHost key={current.id} request={current} />;
  }
  return <AlertHost key={current.id} request={current} />;
};
